import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Contact from "@/components/Contact";
import SEOHead from "@/components/SEOHead";
import NotFound from "@/pages/NotFound";
import { Button } from "@/components/ui/button";
import { articles, getArticle } from "@/data/articles";

const baseUrl = "https://playfieldvr.nl";

const ArticleDetail = () => {
  const { articleSlug } = useParams();
  const article = getArticle(articleSlug);

  if (!article) return <NotFound />;

  const url = `${baseUrl}/kennisbank/${article.slug}`;
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.h1,
    description: article.description,
    datePublished: article.datePublished,
    dateModified: article.dateModified ?? article.datePublished,
    inLanguage: "nl-NL",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: "PlayfieldVR", url: baseUrl },
    publisher: {
      "@type": "Organization",
      name: "PlayfieldVR",
      url: baseUrl,
      logo: {
        "@type": "ImageObject",
        url: `${baseUrl}/playfield-logo.png`,
      },
    },
  };

  const others = articles.filter((a) => a.slug !== article.slug);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={article.title}
        description={article.description}
        canonicalPath={`/kennisbank/${article.slug}`}
        structuredData={structuredData}
      />
      <Navigation />

      <main>
        <article className="container mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/kennisbank"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar de kennisbank
            </Link>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              {article.h1}
            </h1>

            <p className="text-sm text-muted-foreground mb-8">
              {article.readingTime} leestijd
            </p>

            <p className="text-lg text-muted-foreground mb-10">{article.excerpt}</p>

            {article.sections.map((section) => (
              <section key={section.heading} className="mb-10">
                <h2 className="text-2xl font-semibold text-foreground mb-3">
                  {section.heading}
                </h2>
                {section.body.map((paragraph, index) => (
                  <p key={index} className="text-muted-foreground mb-3">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <section className="mb-12 p-6 rounded-lg border border-border bg-card/50">
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Meer weten over de mogelijkheden?
              </h2>
              <p className="text-muted-foreground mb-4">
                Vertel ons wanneer, waar en met hoeveel mensen je wilt spelen. Je
                ontvangt doorgaans binnen één werkdag een voorstel op maat.
              </p>
              <Button asChild>
                <a href="#contact-form">Neem contact op</a>
              </Button>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                Relevante diensten
              </h2>
              <ul className="space-y-2">
                {article.related.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-primary hover:underline">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            {others.length > 0 && (
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">
                  Verder lezen
                </h2>
                <ul className="space-y-2">
                  {others.map((other) => (
                    <li key={other.slug}>
                      <Link
                        to={`/kennisbank/${other.slug}`}
                        className="inline-flex items-center gap-2 text-primary hover:underline"
                      >
                        {other.h1}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </article>

        <Contact />
      </main>

      <Footer />
    </div>
  );
};

export default ArticleDetail;
